"use client";
import { useSignIn, useSignUp, useUser } from "@clerk/nextjs";
import { Loader } from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import type React from "react";
import { useEffect, useState } from "react";
import { Button } from "@/app/components/CustomButton";
import GoogleIcon from "../../../../app/public/icons/google.svg";
import { AuthCustomInput } from "../../../components/AuthCustomInput";

interface VerificationFormProps {
	signUp: ReturnType<typeof useSignUp>["signUp"];
	loading: boolean;
	setLoading: (loading: boolean) => void;
	setError: (error: string | null) => void;
	error: string | null;
	router: ReturnType<typeof useRouter>;
}

const VerificationForm = ({
	signUp,
	loading,
	setLoading,
	setError,
	error,
	router,
}: VerificationFormProps) => {
	const [verificationCode, setVerificationCode] = useState("");
	const [resending, setResending] = useState(false);

	async function onVerify(e: React.FormEvent) {
		e.preventDefault();
		if (loading) {
			return;
		}

		setError(null);
		setLoading(true);

		try {
			const { error: verifyError } = await signUp.verifications.verifyEmailCode(
				{
					code: verificationCode,
				},
			);

			if (verifyError) {
				if (verifyError.code === "form_code_incorrect") {
					setError("Incorrect verification code. Please try again.");
				} else if (verifyError.code === "client_state_invalid") {
					setError("Session expired. Please refresh the page and start over.");
				} else {
					setError(
						verifyError.message || "Verification failed. Please try again.",
					);
				}
				return;
			}

			if (signUp.status === "complete") {
				await signUp.finalize();
				router.push("/overview");
			} else {
				setError("Verification incomplete. Please try again.");
			}
		} finally {
			setLoading(false);
		}
	}

	async function resendCode() {
		if (resending) {
			return;
		}

		setError(null);
		setResending(true);

		try {
			const { error: resendError } = await signUp.verifications.sendEmailCode();
			if (resendError) {
				setError(
					resendError.message || "Failed to resend code. Please try again.",
				);
			}
		} finally {
			setResending(false);
		}
	}

	return (
		<form onSubmit={onVerify} className="grid gap-4">
			<AuthCustomInput
				label="Verification Code"
				type="text"
				placeholder="Enter verification code"
				required
				value={verificationCode}
				onChange={(e) => setVerificationCode(e.target.value)}
			/>{" "}
			<p className="text-xs text-[#6B7280] -mt-2">
				Check your email for the verification code
			</p>{" "}
			<Button
				type="submit"
				className="w-full rounded-full !bg-[#FFD24D] !text-[#1A2314] hover:!bg-[#FFD24D]/90"
				disabled={loading}
			>
				{loading ? (
					<>
						<Loader className="mr-2 h-4 w-4 animate-spin text-[#1A2314]" />
						Verifying...
					</>
				) : (
					"Verify"
				)}
			</Button>
			<Button
				type="button"
				variant="outline"
				className="w-full rounded-full border border-[#1A231414] bg-white text-[#1A2314] ring-0 hover:bg-stone-100 hover:text-[#1A2314]"
				disabled={resending || loading}
				onClick={resendCode}
			>
				{resending ? (
					<>
						<Loader className="mr-2 h-4 w-4 animate-spin text-[#1A2314]" />
						Resending...
					</>
				) : (
					"Resend Code"
				)}
			</Button>
			{error && <p className="text-center text-[#DC2626] text-sm">{error}</p>}
		</form>
	);
};

const Loading = () => (
	<div className="flex w-full justify-center gap-6">
		<Loader className="h-[48px] w-[48px] animate-spin text-[#1A2314]" />
	</div>
);

const SignUpPage = () => {
	const router = useRouter();
	const { isSignedIn, isLoaded } = useUser();
	const { signUp } = useSignUp();
	const { signIn } = useSignIn();
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [oauthLoading, setOauthLoading] = useState<"oauth_google" | null>(null);
	const [showVerification, setShowVerification] = useState(false);

	// biome-ignore lint/correctness/useExhaustiveDependencies: This is a false positive
	useEffect(() => {
		if (isSignedIn && isLoaded) {
			router.push("/overview");
		}
	}, [isLoaded]);

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (loading) {
			return;
		}

		setError(null);
		setLoading(true);

		try {
			const { error } = await signUp.create({
				emailAddress: email,
				password,
				firstName,
				lastName,
			});

			if (error) {
				if (error.code === "verification_already_verified") {
					setShowVerification(true);
					setError(null);
				} else if (error.code === "client_state_invalid") {
					setError("Session expired. Please refresh the page and try again.");
				} else {
					setError(error.message || "Something went wrong. Please try again.");
				}
				return;
			}

			if (signUp.status === "complete") {
				await signUp.finalize();
				router.push("/overview");
				return;
			}

			if (
				signUp.verifications.emailAddress?.status !== "verified" &&
				signUp.verifications.emailAddress?.status !== "transferable"
			) {
				await signUp.verifications.sendEmailCode();
			}
			setShowVerification(true);
		} finally {
			setLoading(false);
		}
	}

	async function signUpWithOAuth(strategy: "oauth_google") {
		setError(null);
		setOauthLoading(strategy);
		const { error } = await signUp.sso({
			strategy,
			redirectUrl: "/overview",
			redirectCallbackUrl: "/sign-in/sso-callback",
		});
		if (error) {
			if (error.code === "external_account_exists") {
				await signIn.sso({
					strategy,
					redirectUrl: "/overview",
					redirectCallbackUrl: "/sign-in/sso-callback",
				});
			} else {
				setError(error.message || "OAuth sign-in failed.");
				setOauthLoading(null);
			}
		}
	}

	const SignUpForm = (
		<div data-testid="sign-up-form">
			{showVerification ? (
				<div>
					<div className="mb-4 text-base font-medium text-[#1A2314]">
						Enter your verification code
					</div>
					<VerificationForm
						signUp={signUp}
						loading={loading}
						setLoading={setLoading}
						setError={setError}
						error={error}
						router={router}
					/>
				</div>
			) : (
				<form onSubmit={onSubmit}>
					<div className="grid gap-4">
						<div className="grid gap-4">
							<AuthCustomInput
								label="Name"
								type="text"
								placeholder="Enter your name"
								value={firstName}
								onChange={(e) => setFirstName(e.target.value)}
							/>
							<AuthCustomInput
								label="Surname"
								type="text"
								placeholder="Enter your surname"
								value={lastName}
								onChange={(e) => setLastName(e.target.value)}
							/>
							<AuthCustomInput
								label="Email"
								type="email"
								placeholder="Enter your email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
							<AuthCustomInput
								label="Password"
								type="password"
								placeholder="Enter your password"
								required
								value={password}
								onChange={(e) => setPassword(e.target.value)}
							/>
							<Button
								type="submit"
								className="mt-2 h-11 w-full rounded-full !bg-[#004A7C] !text-white shadow-[0_1px_2px_rgba(10,13,18,0.05)] hover:!bg-[#003366]/90"
								disabled={loading}
							>
								{loading ? (
									<>
										<Loader className="mr-2 h-4 w-4 animate-spin text-white" />
										Signing up...
									</>
								) : (
									"Get started"
								)}
							</Button>
							{error && (
								<p className="text-center text-[#DC2626] text-sm">{error}</p>
							)}
						</div>
						<Button
							variant="outline"
							className="h-11 w-full rounded-full bg-white text-[#1A2314] ring-1 ring-[#D5D7DA] hover:bg-[#F5F5F0] hover:text-[#1A2314]"
							onClick={() => signUpWithOAuth("oauth_google")}
							type="button"
							disabled={oauthLoading === "oauth_google"}
						>
							{oauthLoading === "oauth_google" ? (
								<Loader className="mr-2 h-4 w-4 animate-spin text-[#1A2314] rounded-full" />
							) : (
								<Image
									src={GoogleIcon}
									alt="Google"
									width={16}
									height={16}
									className="mr-2"
								/>
							)}
							Continue with Google
						</Button>
					</div>
				</form>
			)}
		</div>
	);

	return isLoaded && !isSignedIn ? SignUpForm : <Loading />;
};

export default SignUpPage;
